import { useEligibleOffers } from "components/offers/hooks";
import { usePolicyId, useUser } from "data/ss-data-hooks";
import { useFlags } from "utils";

export type NotificationDetail = {
  offerTitle: string;
  offerDescription: string;
  offerInternalName: string;
  path?: string;
};

const offersDeclinedMap: Record<string, string> = {
  "wind-defense": "wind-defense-landing-declined",
  "ting-offer": "ting-landing-declined",
};

// Titles must match the offering internal name
const titleDescriptionMap: Record<
  string,
  { title: string; description: string }
> = {
  "marketing-optin": {
    title: "Get Your Free Home Maintenance Checklist",
    description: `Subscribe to our mailing list for SageSure offers, news, and expert tips to protect your home, delivered to your inbox.`,
  },
  "wind-defense": {
    title: "Get Free Roof Protection Before Hurricane Season Hits",
    description: `Sign up for Wind Defense and we'll take care of debris removal, gutter cleaning, and shingle inspection and resealing.`,
  },
};

export const useNotifications = () => {
  const { notificationPilot } = useFlags();
  const notificationPilotEnabled = notificationPilot === true;

  const user = useUser();
  const selectedPolicyId = usePolicyId();
  const { eligibleOffers } = useEligibleOffers(
    notificationPilotEnabled ? selectedPolicyId : undefined,
  );

  // if flag is off then skip both the offers fetch above and building the marketing-optin notification below
  if (!notificationPilotEnabled) {
    return [];
  }

  const notifications: NotificationDetail[] = eligibleOffers.unenrolledOffers
    .filter((offer) => offer.offeringInternalName in titleDescriptionMap)
    .filter((offer) => {
      const declinedPreferenceName =
        offersDeclinedMap[offer.offeringInternalName];
      const declinedPreference = user?.accountPreferences?.find(
        (pref) => pref.preferenceName === declinedPreferenceName,
      );
      return !declinedPreference?.preferenceValue;
    })
    .map((offer) => ({
      offerTitle: titleDescriptionMap[offer.offeringInternalName]?.title,
      offerDescription:
        titleDescriptionMap[offer.offeringInternalName]?.description,
      offerInternalName: offer.offeringInternalName,
      path: offer.offerUrl,
    }));

  const marketingPreference = user?.accountPreferences?.find(
    (pref) => pref.preferenceName === "marketing-optin",
  );
  const isMarketingEligible = !marketingPreference?.preferenceValue;

  if (isMarketingEligible) {
    const map = titleDescriptionMap["marketing-optin"];
    const marketingNotification = {
      offerTitle: map.title,
      offerDescription: map.description,
      offerInternalName: "marketing-optin",
    };
    notifications.push(marketingNotification);
  }

  return notifications;
};
